import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { SxProps } from '@mui/system';

import { COLOR_PRIMARY_DARK, COLOR_GREEN, COLOR_ACCENT_1 } from 'src/const';
import { DotLoader } from 'src/App/Components/Loaders';

interface LisingCardPropTypes {
  listingID: number;
  title: string;
  address: string;
  image: string;
  synced: boolean;
  openListingCalendar: any;
  openListingCompSet: any;
  handleSyncStatusChange: any;
}

const ListingCard = ({
  listingID,
  title,
  address,
  image,
  openListingCalendar,
  openListingCompSet,
  synced,
  handleSyncStatusChange,
}: LisingCardPropTypes) => {
  const [syncStatus, setSyncStatus] = useState<boolean>(synced);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const CustomSwitch = styled((props: any) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: COLOR_GREEN,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: COLOR_GREEN,
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }));

  const buttonStyle = (color: string, hoverColor: string): SxProps => ({
    fontSize: '12px',
    width: '100%',
    padding: '20px',
    paddingTop: '10px',
    paddingBottom: '10px',
    borderRadius: '50px',
    background: color,
    boxShadow: '0 0 10px #ccc',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: hoverColor,
      boxShadow: '0 0 5px #aaa',
    },
  });

  const handleSyncChange = async e => {
    setIsSyncing(true);
    await handleSyncStatusChange(e, listingID);
    setIsSyncing(false);
    setSyncStatus(prev => !prev);
  };

  return (
    <Card
      elevation={0}
      sx={{
        // maxWidth: 300,
        borderRadius: '10px',
        filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.2))',
      }}
    >
      <Backdrop
        sx={{
          color: '#fff',
          background: '#0007',
          backdropFilter: 'blur(1px)',
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
        open={isSyncing}
      >
        <div style={{ textAlign: 'center' }}>
          {/* <CircularProgress color="inherit" size={50} thickness={2} /> */}
          <DotLoader />
        </div>
      </Backdrop>

      <CardMedia
        component="img"
        height="190"
        image={image.includes('?aki_policy=small') ? image.split('?aki_policy=small')[0] : image}
        alt="Listing Image"
        sx={{
          padding: '5%',
          width: '90%',
          borderRadius: '30px',
          marginBottom: '-10px',
        }}
      />
      <CardContent sx={{ marginLeft: '2.5%' }}>
        <Typography
          variant="h5"
          component="h4"
          sx={{
            fontWeight: 'bold',
            fontSize: '20px',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          component="h6"
          sx={{
            color: '#888',
            marginBottom: '20px',
          }}
          gutterBottom
        >
          {address}
        </Typography>
        <Box
          sx={{
            padding: 0,
            margin: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="p" sx={{ fontWeight: 900, color: '#037A90' }}>
            Sync
          </Typography>
          <CustomSwitch
            checked={syncStatus}
            onChange={e => {
              handleSyncChange(e);
            }}
          />
        </Box>
        <Grid
          container
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: '20px', marginBottom: '10px' }}
        >
          <Grid item xs={12} lg={6}>
            <Button
              variant="contained"
              onClick={openListingCalendar}
              sx={buttonStyle(COLOR_PRIMARY_DARK, COLOR_PRIMARY_DARK + 'CC')} // CC -> opacity hex code
            >
              Review Calendar
            </Button>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Button
              variant="contained"
              onClick={openListingCompSet}
              // sx={buttonStyle('#65C466', '#65bb66')}
              sx={buttonStyle(COLOR_GREEN, COLOR_GREEN + 'CC')}
            >
              Review CompSet
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ListingCard;
